import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { isEmpty } from "lodash-es";
import { type FC, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { fromISO8601_DATE, toISO8601_DATE } from "../../../utils/iso8601";
import { uuidv4 } from "../../../utils/uuid";
import { useSaveEvent } from "../hooks/use-save-event";
import { type Event, eventSchema, type OptionalInterval } from "../types";
import { CustomRepeat } from "./custom-repeat";
import { RepeatSelect } from "./repeat-select";

/** Form for editing `event`s; responsible for loading and saving */
export const EventForm: FC<{
  onChange: (event: Event) => void;
  onCancel: () => void;
}> = ({ onChange, onCancel }) => {
  const defaultValues = useMemo(
    () => ({
      id: uuidv4(),
      key: uuidv4(),
      source: "WEB",
      title: "",
      date: toISO8601_DATE(new Date())!,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      repeat: "never" as OptionalInterval,
      number_of_repeats: undefined,
    }),
    [],
  );

  const [repeat, setRepeat] = useState<Event["repeat"] | "custom">(defaultValues.repeat);

  const methods = useForm({ defaultValues, resolver: yupResolver(eventSchema) });

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
  } = methods;

  const { error, save: saveValues } = useSaveEvent({ onChange, reset: () => reset(defaultValues) });

  useEffect(() => {
    setValue("repeat", repeat === "custom" ? undefined : repeat);

    if (repeat === "custom") setValue("number_of_repeats", undefined);
    else if (repeat === "never") setValue("number_of_repeats", 0);
    else setValue("number_of_repeats", 100);
  }, [repeat, setValue]);

  if (error.error) return <Alert severity="error">{error.error}</Alert>;

  return (
    <FormProvider {...methods}>
      <Stack direction="column" spacing={2}>
        {error.validationError && <Alert severity="warning">{error.validationError}</Alert>}
        <Stack direction="row" spacing={2}>
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState, formState: { submitCount } }) => (
              <TextField
                {...field}
                label="Title"
                error={Boolean(submitCount > 0 && fieldState.error)}
                helperText={submitCount > 0 && fieldState.error?.message}
                sx={{ mr: 2 }}
                id="event-title-input"
                slotProps={{
                  htmlInput: {
                    "data-analytics-id": "create-event-form-title-input",
                  },
                }}
              />
            )}
          />
          <Controller
            name="date"
            control={control}
            render={({ field, fieldState }) => (
              <DatePicker
                {...field}
                value={fromISO8601_DATE(field.value)}
                onChange={(value) => field.onChange(toISO8601_DATE(value))}
                slotProps={{
                  textField: {
                    id: "event-date-input",
                    error: Boolean(fieldState.error),
                    helperText: fieldState.error?.message,
                    sx: { mr: 2 },
                    inputProps: {
                      "data-analytics-id": "create-event-form-date-input",
                    },
                  },
                }}
              />
            )}
          />
          <RepeatSelect repeat={repeat} setRepeat={setRepeat} />
        </Stack>
        {repeat === "custom" && <CustomRepeat hasError={!isEmpty(errors.number_of_repeats)} />}
        <Stack direction="row-reverse" spacing={2}>
          <Button
            data-analytics-id="create-event-form-dialog-create"
            type="button"
            loading={isSubmitting}
            variant="contained"
            onClick={(e) => void handleSubmit(saveValues)(e).catch(console.error)}
          >
            Create event
          </Button>
          <Button
            data-analytics-id="create-event-form-dialog-cancel"
            type="button"
            variant="outlined"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
};
